// ===========================================
// SECURITY MODULE
// Centralizes all security-related functions
// ===========================================

import DOMPurify from 'dompurify';

/**
 * Safely sets the innerHTML of an element after sanitizing the content.
 * @param {HTMLElement} element - The DOM element to update.
 * @param {string} htmlContent - The potentially unsafe HTML string.
 */
export function safeSetHTML(element, htmlContent) {
    if (!element) {
        console.warn('safeSetHTML: Element is null or undefined');
        return;
    }
    element.innerHTML = DOMPurify.sanitize(htmlContent, {
        ADD_ATTR: ['style', 'data-context-color', 'data-type', 'data-icon', 'data-color', 'data-step', 'data-context-id']
    });
}

/**
 * Returns a sanitized version of the string/HTML.
 * @param {string} content - The unsafe content.
 * @returns {string} The sanitized content.
 */
export function sanitizeString(content) {
    return DOMPurify.sanitize(content);
}

/**
 * Generates a SHA-256 / Checksum signature for a data payload to prevent tampering in LocalStorage.
 * @param {Object} data - Plain object payload
 * @returns {string} Checksum string
 */
export function generateChecksum(data) {
    try {
        const copy = { ...data };
        delete copy._signature;
        const jsonStr = JSON.stringify(copy) + '_DETECTIMATICAS_SECRET_KEY_v1';
        let hash = 0;
        for (let i = 0; i < jsonStr.length; i++) {
            const char = jsonStr.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash |= 0; // Convert to 32bit integer
        }
        return 'sig_' + Math.abs(hash).toString(36);
    } catch (e) {
        return 'sig_fallback';
    }
}

/**
 * Verifies if payload matches its integrity signature
 * @param {Object} payload - Object containing _signature property
 * @returns {boolean} True if payload signature is valid or signature is missing in legacy data
 */
export function verifyIntegrity(payload) {
    if (!payload || typeof payload !== 'object') return false;
    if (!payload._signature) return true; // Legacy un-signed data compatibility
    const expected = generateChecksum(payload);
    return payload._signature === expected;
}

// Namespace encapsulation for Publisher / B2B Embed Safety
if (typeof window !== 'undefined') {
    window.Detectimaticas = window.Detectimaticas || {};
    window.Detectimaticas.Security = { safeSetHTML, sanitizeString, generateChecksum, verifyIntegrity };

    // Legacy fallback aliases
    window.safeSetHTML = safeSetHTML;
    window.sanitizeString = sanitizeString;
    window.generateChecksum = generateChecksum;
    window.verifyIntegrity = verifyIntegrity;
}
